import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import { useDistributorWorkspace } from '@/contexts/DistributorWorkspaceContext'
import { ApiClientError } from '@/lib/api/client'
import {
  createMyDistributorAffiliate,
  fetchMyDistributorAffiliates,
  formatMyDistributorCurrency,
  updateMyDistributorAffiliate,
} from '@/lib/my-distributor'
import { DistributorAffiliateRegistrationModal } from '@/pages/SouDistribuidorPage/DistributorAffiliateRegistrationModal'
import { RequireDistributorWorkspace } from '@/pages/SouDistribuidorPage/RequireDistributorWorkspace'
import type {
  CreateMyDistributorAffiliateInput,
  MyDistributorAffiliateRegistration,
  UpdateMyDistributorAffiliateInput,
} from '@/types/my-distributor'
import styles from './SouDistribuidorPage.module.css'

function SouDistribuidorAfiliadosListaPageContent() {
  const { selectedProgram, campaignId } = useDistributorWorkspace()
  const [registrations, setRegistrations] = useState<MyDistributorAffiliateRegistration[]>([])
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<MyDistributorAffiliateRegistration | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!selectedProgram) return
    setIsLoading(true)
    setError(null)
    try {
      const res = await fetchMyDistributorAffiliates(selectedProgram.distributor_id, {
        campaign_id: campaignId ?? undefined,
      })
      setRegistrations(res.registrations)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar afiliados.')
    } finally {
      setIsLoading(false)
    }
  }, [selectedProgram, campaignId])

  useEffect(() => {
    void load()
  }, [load])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return registrations
    return registrations.filter(
      (registration) =>
        registration.affiliate_name.toLowerCase().includes(term) ||
        registration.tracking_code.toLowerCase().includes(term)
    )
  }, [registrations, search])

  const openCreate = () => {
    setEditing(null)
    setIsModalOpen(true)
    setActionError(null)
  }

  const openEdit = (registration: MyDistributorAffiliateRegistration) => {
    setEditing(registration)
    setIsModalOpen(true)
    setActionError(null)
  }

  const closeModal = () => {
    if (isSubmitting) return
    setIsModalOpen(false)
    setEditing(null)
  }

  const handleSubmit = async (
    data: CreateMyDistributorAffiliateInput | UpdateMyDistributorAffiliateInput,
    registrationId?: number
  ) => {
    if (!selectedProgram) return
    setIsSubmitting(true)
    setActionError(null)
    try {
      if (registrationId != null) {
        await updateMyDistributorAffiliate(
          selectedProgram.distributor_id,
          registrationId,
          data as UpdateMyDistributorAffiliateInput
        )
      } else {
        await createMyDistributorAffiliate(
          selectedProgram.distributor_id,
          data as CreateMyDistributorAffiliateInput
        )
      }
      setIsModalOpen(false)
      setEditing(null)
      await load()
    } catch (err) {
      setActionError(err instanceof ApiClientError ? err.message : 'Erro ao salvar afiliado.')
      throw err
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className={styles.centered}>
        <Icon icon="mdi:loading" className={styles.spinner} />
        <span>Carregando...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.centered}>
        <Icon icon="mdi:alert-circle-outline" className={styles.errorIcon} />
        <span>{error}</span>
        <button type="button" className={styles.retryBtn} onClick={() => void load()}>
          Tentar novamente
        </button>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <input
          type="search"
          className={styles.select}
          style={{ flex: 1, maxWidth: 360 }}
          placeholder="Buscar por nome ou código..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          aria-label="Buscar afiliados"
        />
        <button type="button" className={styles.primaryBtn} onClick={openCreate}>
          <Icon icon="mdi:plus" />
          Novo afiliado
        </button>
      </div>

      {actionError ? (
        <p className={styles.programMeta} style={{ color: '#ef4444' }} role="alert">
          {actionError}
        </p>
      ) : null}

      {filtered.length === 0 ? (
        <div className={styles.emptyBlock}>
          <Icon icon="mdi:account-group-outline" />
          <span>Nenhum afiliado encontrado neste contexto.</span>
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Afiliado</th>
                <th>Código</th>
                <th>Comissão</th>
                <th>Campanhas</th>
                <th>Vendas</th>
                <th>Status</th>
                <th aria-label="Ações" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((registration) => (
                <tr key={registration.id}>
                  <td>{registration.affiliate_name}</td>
                  <td className={styles.trackingCode}>{registration.tracking_code}</td>
                  <td>{registration.commission_percent}%</td>
                  <td>{registration.all_campaigns ? 'Todas' : registration.allowed_campaigns.length}</td>
                  <td>
                    {registration.metrics?.payments_count ?? 0} ·{' '}
                    {formatMyDistributorCurrency(registration.metrics?.total_sales ?? 0)}
                  </td>
                  <td>
                    <span
                      className={
                        registration.is_active ? styles.badgeActive : styles.badgeInactive
                      }
                    >
                      {registration.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className={styles.retryBtn}
                      onClick={() => openEdit(registration)}
                    >
                      Editar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen && selectedProgram
        ? createPortal(
            <DistributorAffiliateRegistrationModal
              program={selectedProgram}
              registration={editing}
              workspaceCampaignId={campaignId}
              isSubmitting={isSubmitting}
              onClose={closeModal}
              onSubmit={handleSubmit}
            />,
            document.body
          )
        : null}
    </div>
  )
}

export function SouDistribuidorAfiliadosListaPage() {
  return (
    <RequireDistributorWorkspace requireActiveProgram>
      <SouDistribuidorAfiliadosListaPageContent />
    </RequireDistributorWorkspace>
  )
}
